import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useDropzone } from 'react-dropzone';
import { toast } from 'sonner';
import { 
  Upload, FileText, Trash2, Loader2, ArrowRight, Ruler,
  AlertCircle, CheckCircle, X
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api/subbuilder`;

// Reusable DropButton matching Product Data's style
function DrawingDropButton({ onFilesDropped, isUploading, testId }) {
  const fileInputRef = useRef(null);

  const handleFileChange = useCallback((e) => {
    e.stopPropagation();
    const files = Array.from(e.target.files || []);
    if (files.length > 0) onFilesDropped(files);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [onFilesDropped]);

  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) onFilesDropped(acceptedFiles);
  }, [onFilesDropped]);

  const { getRootProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'application/pdf': ['.pdf'] },
    multiple: true,
    disabled: isUploading,
    noClick: true,
    noKeyboard: true,
  });

  return (
    <div {...getRootProps()} className="inline-block">
      <label
        className={`
          inline-flex items-center justify-center whitespace-nowrap rounded-md font-medium
          bg-primary text-primary-foreground hover:bg-primary/90 h-9 px-3 gap-1.5 text-xs transition-all
          ${isUploading ? 'opacity-50 pointer-events-none' : 'cursor-pointer'}
          ${isDragActive ? 'ring-2 ring-primary ring-offset-2 scale-105' : ''}
        `}
        data-testid={testId}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf"
          multiple
          disabled={isUploading}
          onChange={handleFileChange}
          className="sr-only"
        />
        {isUploading ? (
          <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Uploading</>
        ) : (
          <><Upload className="h-3.5 w-3.5" /> Drawings</>
        )}
      </label>
    </div>
  );
}

export default function DrawingsStep({ project, onComplete }) {
  const [submittals, setSubmittals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(null);  const [uploadProgress, setUploadProgress] = useState({});
  const [merging, setMerging] = useState(null);
  const [viewingDrawing, setViewingDrawing] = useState(null);

  useEffect(() => {
    if (!project?.id) return;
    loadSubmittals();
  }, [project?.id]);

  const loadSubmittals = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/projects/${project.id}/project-submittals`);
      const subs = res.data?.submittals || res.data || [];
      setSubmittals(Array.isArray(subs) ? subs : []);
    } catch (e) {
      console.error('Failed to load submittals:', e);
    } finally {
      setLoading(false);
    }
  };

  const parseDrawings = (sub) => {
    try {
      const d = sub.drawings_data;
      if (!d) return [];
      return typeof d === 'string' ? JSON.parse(d) : (Array.isArray(d) ? d : []);
    } catch { return []; }
  };

  const handleUploadDrawings = async (submittalId, files) => {
    if (!files || files.length === 0) return;
    setUploading(submittalId);
    setUploadProgress(prev => ({ ...prev, [submittalId]: { progress: 0, fileName: files[0]?.name || '' } }));
    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const formData = new FormData();
        formData.append('file', file);
        formData.append('doc_type', 'drawing');
        await axios.post(
          `${API}/projects/${project.id}/project-submittals/${submittalId}/upload-drawing`,
          formData,
          {
            headers: { 'Content-Type': 'multipart/form-data' },
            onUploadProgress: (progressEvent) => {
              const fileProgress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              const overallProgress = Math.round(((i * 100) + fileProgress) / files.length);
              setUploadProgress(prev => ({ ...prev, [submittalId]: { progress: overallProgress, fileName: file.name } }));
            }
          }
        );
      }
      toast.success(`${files.length} drawing${files.length > 1 ? 's' : ''} uploaded and added to end of package`);
      await loadSubmittals();
    } catch (e) {
      toast.error('Upload failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setUploadProgress(prev => ({ ...prev, [submittalId]: { progress: 100, fileName: '' } }));
      setTimeout(() => {
        setUploadProgress(prev => { const n = { ...prev }; delete n[submittalId]; return n; });
      }, 1500);
      setUploading(null);
    }
  };

  const handleRemoveDrawing = async (submittalId, drawingIndex) => {
    try {
      await axios.delete(
        `${API}/projects/${project.id}/project-submittals/${submittalId}/drawings/${drawingIndex}`
      );
      toast.success('Drawing removed');
      await loadSubmittals();
    } catch (e) {
      toast.error('Remove failed: ' + (e.response?.data?.detail || e.message));
    }
  };


  const handleContinueToAnnotate = async () => {
    const subsWithDrawings = subsWithPackage.filter(s => parseDrawings(s).length > 0);
    if (subsWithDrawings.length > 0) {
      setMerging('all');
      try {
        for (const sub of subsWithDrawings) {
          await axios.get(`${API}/projects/${project.id}/project-submittals/${sub.id}/full-package-url`);
        }
        toast.success('Drawings merged into submittal package');
      } catch (e) {
        console.error('Merge error:', e);
      } finally {
        setMerging(null);
      }
    }
    onComplete();
  };

  if (loading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  }

  const subsWithPackage = submittals.filter(s => s.package_url || s.status === 'package_built');

  if (subsWithPackage.length === 0) {
    return (
      <Card><CardContent className="py-12 text-center">
        <AlertCircle className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
        <p className="text-muted-foreground">No built submittals yet. Build a package in the Product Data step first.</p>
      </CardContent></Card>
    );
  }

  const totalDrawings = subsWithPackage.reduce((sum, s) => sum + parseDrawings(s).length, 0);

  return (
    <div className="space-y-4" data-testid="drawings-step">
      {/* Header - matching Product Data */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Ruler className="h-5 w-5 text-primary" />
            Drawings
          </CardTitle>
        </CardHeader>
      </Card>

      {/* Submittal Cards - matching Product Data layout */}
      {subsWithPackage.map((sub, index) => {
        const drawings = parseDrawings(sub);
        const isUploading = uploading === sub.id;
        const progress = uploadProgress[sub.id];

        return (
          <Card key={sub.id} className="border-border overflow-hidden" data-testid={`drawings-card-${sub.id}`}>
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className={`
                    h-10 w-10 rounded-lg flex items-center justify-center
                    ${drawings.length > 0 ? 'bg-green-500/20' : 'bg-primary/10'}
                  `}>
                    <span className={`text-lg font-bold ${drawings.length > 0 ? 'text-green-500' : 'text-primary'}`}>{index + 1}</span>
                  </div>
                  <div>
                    <CardTitle className="text-lg flex items-center gap-2">
                      {sub.name || 'Submittal'}
                      {drawings.length > 0 && (
                        <Badge variant="success" className="gap-1 bg-green-500/20 text-green-600">
                          <CheckCircle className="h-3 w-3" />
                          {drawings.length} drawing{drawings.length !== 1 ? 's' : ''}
                        </Badge>
                      )}
                    </CardTitle>
                    <CardDescription className="text-xs">
                      {sub.spec_section}
                    </CardDescription>
                  </div>
                </div>
              </div>
            </CardHeader>

            <CardContent className="space-y-3">
              {/* Action Buttons Row - matching Product Data style */}
              <div className="flex items-center gap-2">
                <DrawingDropButton
                  onFilesDropped={(files) => handleUploadDrawings(sub.id, files)}
                  isUploading={isUploading}
                  testId={`upload-drawings-btn-${index}`}
                />
              </div>

              {/* Upload Progress - matching Product Data style */}
              {progress && (
                <div className="space-y-2 p-2.5 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md" data-testid={`drawing-upload-progress-${index}`}>
                  <div className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin text-blue-600 dark:text-blue-400" />
                    <p className="text-sm text-blue-700 dark:text-blue-300">
                      Adding drawing to end of package...
                    </p>
                  </div>
                  <Progress value={progress.progress} />
                  <p className="text-xs text-muted-foreground text-center">
                    {progress.fileName && <span>{progress.fileName} — </span>}
                    {progress.progress}%
                  </p>
                </div>
              )}

              {/* Drawings List */}
              {drawings.length > 0 && (
                <div className="space-y-1">
                  {drawings.map((d, i) => (
                    <div
                      key={i}
                      className="flex items-center gap-2 p-2 rounded-md bg-muted/30 hover:bg-muted/50 transition-colors group"
                      data-testid={`drawing-item-${sub.id}-${i}`}
                    >
                      <FileText className="h-4 w-4 text-blue-500 flex-shrink-0" />
                      <span className="text-sm flex-1 truncate text-foreground">
                        {d.name || d.filename || `Drawing ${i + 1}`}
                      </span>
                      <button
                        onClick={() => handleRemoveDrawing(sub.id, i)}
                        className="opacity-0 group-hover:opacity-100 transition-opacity text-destructive hover:text-destructive/80 p-1 rounded"
                        title="Remove drawing"
                        data-testid={`remove-drawing-${sub.id}-${i}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}

      {/* Bottom Actions */}
      <div className="flex flex-col gap-3 pt-2">
        <p className="text-sm text-muted-foreground">
          {totalDrawings === 0
            ? 'No drawings uploaded yet. You can skip this step if not needed.'
            : `${totalDrawings} drawing${totalDrawings !== 1 ? 's' : ''} will be appended after product data.`
          }
        </p>
        <div className="flex justify-end">
          <Button variant="nav"
            onClick={handleContinueToAnnotate}
            disabled={!!merging}
            className="btn-primary"
            data-testid="continue-to-annotate-btn"
          >
            {merging ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Merging...</>
            ) : (
              <>Continue to Annotate <ArrowRight className="h-4 w-4 ml-2" /></>
            )}
          </Button>
        </div>
      </div>

    </div>
  );
}
